export type PixelPalette = Record<string, string>

export type PixelRun = readonly [y: number, x0: number, x1: number]

export type PixelPoint = readonly [x: number, y: number]

export type PixelMatrix = Array<Array<string>>

export type PixelMask = Array<Array<boolean>>

export interface PixelLayer {
    fill: string
    d: string
}

interface PaintOptions {
    fill: string
    top?: string
    bottom?: string
    bottomDepth?: number
    outline?: string
    skipOutline?: (x: number, y: number) => boolean
}

/**
 * Turns rows of characters into one SVG path per colour.
 * Every character found in the palette becomes a 1x1 pixel, horizontal runs are merged.
 * Characters missing from the palette (for example '.') stay transparent.
 */
export const rowsToLayers = (rows: ReadonlyArray<string>, palette: PixelPalette): Array<PixelLayer> => {
    const paths = new Map<string, Array<string>>()

    rows.forEach((row, y) => {
        let x = 0

        while (x < row.length) {
            const key = row[x]
            const fill = palette[key]

            if (!fill) {
                x += 1
                continue
            }

            let end = x + 1

            while (end < row.length && row[end] === key) end += 1

            const width = end - x
            const parts = paths.get(fill) ?? []

            parts.push(`M${x} ${y}h${width}v1h-${width}z`)
            paths.set(fill, parts)
            x = end
        }
    })

    return Array.from(paths, ([fill, parts]) => ({ fill, d: parts.join('') }))
}

export const getRowsSize = (rows: ReadonlyArray<string>): { width: number, height: number } => {
    return {
        width: rows.reduce((max, row) => Math.max(max, row.length), 0),
        height: rows.length
    }
}

export const createMatrix = (width: number, height: number): PixelMatrix => {
    return Array.from({ length: height }, () => Array.from({ length: width }, () => '.'))
}

export const matrixToRows = (matrix: PixelMatrix): Array<string> => {
    return matrix.map(row => row.join(''))
}

export const maskFromRuns = (runs: ReadonlyArray<PixelRun>, width: number, height: number, offset = 0): PixelMask => {
    const mask: PixelMask = Array.from({ length: height }, () => Array.from({ length: width }, () => false))

    runs.forEach(([y, x0, x1]) => {
        for (let x = x0; x <= x1; x += 1) {
            const row = mask[y + offset]

            if (row && x + offset < width) row[x + offset] = true
        }
    })

    return mask
}

/**
 * Paints a silhouette into a matrix: a fill colour, optional top highlight and bottom shade,
 * and an optional 1px outline around it (4-neighbour, so diagonals stay pixel-perfect).
 */
export const paintMask = (matrix: PixelMatrix, mask: PixelMask, options: PaintOptions): void => {
    const height = mask.length
    const width = mask[0]?.length ?? 0
    const depth = options.bottomDepth ?? 1

    const filled = (x: number, y: number): boolean => {
        return y >= 0 && y < height && x >= 0 && x < width && mask[y][x]
    }

    for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
            if (filled(x, y)) {
                let shaded = false

                for (let step = 1; step <= depth; step += 1) {
                    if (!filled(x, y + step)) shaded = true
                }

                if (options.bottom && shaded) matrix[y][x] = options.bottom
                else if (options.top && !filled(x, y - 1)) matrix[y][x] = options.top
                else matrix[y][x] = options.fill

                continue
            }

            if (!options.outline || options.skipOutline?.(x, y)) continue

            const touches = filled(x + 1, y) || filled(x - 1, y) || filled(x, y + 1) || filled(x, y - 1)

            if (touches) matrix[y][x] = options.outline
        }
    }
}

export const stamp = (matrix: PixelMatrix, points: ReadonlyArray<PixelPoint>, key: string, offset = 0): void => {
    points.forEach(([x, y]) => {
        const row = matrix[y + offset]

        if (row && x + offset >= 0 && x + offset < row.length) row[x + offset] = key
    })
}

export const stampRuns = (matrix: PixelMatrix, runs: ReadonlyArray<PixelRun>, key: string, offset = 0): void => {
    runs.forEach(([y, x0, x1]) => {
        for (let x = x0; x <= x1; x += 1) stamp(matrix, [[x, y]], key, offset)
    })
}

/** Integer-only line rasteriser, so server and browser always draw the same pixels. */
export const pixelLine = (x0: number, y0: number, x1: number, y1: number): Array<PixelPoint> => {
    const points: Array<PixelPoint> = []
    const dx = Math.abs(x1 - x0)
    const dy = -Math.abs(y1 - y0)
    const sx = x0 < x1 ? 1 : -1
    const sy = y0 < y1 ? 1 : -1

    let error = dx + dy
    let x = x0
    let y = y0

    for (let guard = 0; guard < 512; guard += 1) {
        points.push([x, y])

        if (x === x1 && y === y1) break

        const doubled = 2 * error

        if (doubled >= dy) {
            error += dy
            x += sx
        }

        if (doubled <= dx) {
            error += dx
            y += sy
        }
    }

    return points
}

/** Small deterministic PRNG (mulberry32). Same seed, same sequence, on every engine. */
export const seededRandom = (seed: number): (() => number) => {
    let state = seed | 0

    return () => {
        state = (state + 0x6d2b79f5) | 0

        let t = Math.imul(state ^ (state >>> 15), 1 | state)

        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t

        return ((t ^ (t >>> 14)) >>> 0) / 4294967296
    }
}
